/**
 * Pure board model: which words are on the board, what each one looks like
 * (its colour, size and whether it is a hollow ring) and which links join
 * them. Shared by the live board and the report mini-graph.
 */
import type { MoveJudgement } from "../../utils/gameReportUtils";

export interface BoardPalette {
  start: string;
  target: string;
  current: string;
  path: string;
  gold: string;
  purple: string;
  ai: string;
}

export type GameStatus = "idle" | "loading" | "playing" | "won" | "given_up";
export type NodeRole =
  | "start"
  | "target"
  | "gold"
  | "purple"
  | "goldOverlay"
  | "purpleOverlay"
  | "aiOverlay"
  | "path"
  | "other";
/** Edge kinds: the player path, the gold path, the Local Optimal route, the AI path. */
export type LinkType = "player" | "gold" | "localOptimal" | "ai";

export interface BoardNode {
  id: string;
  /** World (t-SNE) coordinates. */
  x: number;
  y: number;
  role: NodeRole;
  /** Body colour; for a hollow node, its ring colour. */
  fill: string;
  /** The target draws as an empty ring; every other node is a solid disc. */
  hollow: boolean;
  /** A won board's target: its ring is filled with the target colour. */
  filled: boolean;
  /** Which end of the puzzle this word is, if either. */
  endpoint: "start" | "target" | null;
  isCurrent: boolean;
  /** The current node of a game in progress breathes; never on a finished board. */
  breathing: boolean;
  /** Start, target and current draw larger. */
  focus: boolean;
  inPlayerPath: boolean;
  /** Position in the player path (first occurrence), or -1. */
  pathIndex: number;
}

export interface BoardLink {
  key: string;
  type: LinkType;
  a: string;
  b: string;
  /** Step index along its own path. */
  index: number;
  /** Streak level of a player edge (0 = not in the live streak). */
  level: number;
}

export interface BoardInput {
  position: (word: string) => [number, number] | null;
  startWord: string;
  targetWord: string;
  currentWord: string | null;
  playerPath: string[];
  /** The gold path. */
  optimalPath: string[];
  /** The Local Optimal route from the current word. */
  suggestedPath: string[];
  aiPath: string[];
  optimalChoices: MoveJudgement[];
  status: GameStatus;
  /** Overlay switches (PathDisplayMode keys, Ruling R6): optimal = gold path, suggested = Local Optimal route. */
  display: {
    player: boolean;
    optimal: boolean;
    suggested: boolean;
    ai: boolean;
  };
  palette: BoardPalette;
  /** Streak level per player edge (see game/streak.ts); defaults to 0. */
  edgeLevels?: number[];
  /** The current word's next-word options (live board only). */
  options?: string[];
}

/** A next-word option of the current word, drawn as a candidate node. */
export interface BoardOption {
  id: string;
  x: number;
  y: number;
  /** Already on the board as a path/start/target word: spoke only. */
  existing: boolean;
}

export interface BoardModel {
  nodes: BoardNode[];
  links: BoardLink[];
  current: string | null;
  won: boolean;
  /** Empty unless a game is in progress (never on won/given-up boards). */
  options: BoardOption[];
}

const OTHER_FILL = "#AAAAAA";

/** Global/Local Optimal kind per path word: the player's move landing on it. */
function judgedMovesFor(path: string[], choices: MoveJudgement[]) {
  const out = new Map<string, "global" | "local">();
  choices.forEach((c, i) => {
    if (path[i] !== c.playerPosition || path[i + 1] !== c.playerChose) return;
    if (c.isGlobalOptimal) out.set(c.playerChose, "global");
    else if (c.isLocalOptimal) out.set(c.playerChose, "local");
  });
  return out;
}

export function buildBoardModel(input: BoardInput): BoardModel {
  const { startWord, targetWord, playerPath, display, palette, status } = input;
  const won = status === "won";
  const current = input.currentWord;
  const words: string[] = [];
  const add = (w: string) => {
    if (!words.includes(w)) words.push(w);
  };
  add(startWord);
  add(targetWord);
  if (current) add(current);
  if (display.player) playerPath.forEach(add);
  if (display.optimal) input.optimalPath.forEach(add);
  if (display.suggested) input.suggestedPath.forEach(add);
  if (display.ai) input.aiPath.forEach(add);

  const judgedMoves = judgedMovesFor(playerPath, input.optimalChoices);
  const shownPath = display.player ? playerPath : [];
  const roleOf = (w: string): NodeRole => {
    if (w === startWord) return "start";
    if (w === targetWord) return "target";
    const move = shownPath.includes(w) ? judgedMoves.get(w) : undefined;
    if (move === "global") return "gold";
    if (move === "local") return "purple";
    if (display.optimal && input.optimalPath.includes(w)) return "goldOverlay";
    if (display.suggested && input.suggestedPath.includes(w))
      return "purpleOverlay";
    if (display.ai && input.aiPath.includes(w)) return "aiOverlay";
    if (shownPath.includes(w)) return "path";
    return "other";
  };
  const fillOf: Record<NodeRole, string> = {
    start: palette.start,
    target: palette.target,
    gold: palette.gold,
    purple: palette.purple,
    goldOverlay: palette.gold,
    purpleOverlay: palette.purple,
    aiOverlay: palette.ai,
    path: palette.path,
    other: OTHER_FILL,
  };

  const nodes: BoardNode[] = [];
  for (const w of words) {
    const pos = input.position(w);
    if (!pos) continue;
    const role = roleOf(w);
    // On a won board the target keeps its target fill, not the current one.
    const isCurrent = w === current && !(won && w === targetWord);
    nodes.push({
      id: w,
      x: pos[0],
      y: pos[1],
      role,
      // Fill precedence: current > start > target > Global/Local Optimal moves > overlays
      // > player path > other.
      fill: isCurrent ? palette.current : fillOf[role],
      hollow: w === targetWord && !isCurrent,
      filled: won && w === targetWord,
      endpoint: w === startWord ? "start" : w === targetWord ? "target" : null,
      isCurrent,
      breathing: isCurrent && status === "playing",
      focus: isCurrent || w === startWord || w === targetWord,
      inPlayerPath: playerPath.includes(w),
      pathIndex: playerPath.indexOf(w),
    });
  }
  nodes.sort((a, b) => Number(a.isCurrent) - Number(b.isCurrent));

  const present = new Set(nodes.map((n) => n.id));
  const links: BoardLink[] = [];
  const seq = (path: string[], type: LinkType, on: boolean) => {
    if (!on) return;
    for (let i = 0; i < path.length - 1; i++) {
      const a = path[i];
      const b = path[i + 1];
      if (!present.has(a) || !present.has(b)) continue;
      const level = type === "player" ? (input.edgeLevels?.[i] ?? 0) : 0;
      links.push({
        key: `${type}-${i}-${a}-${b}`,
        type,
        a,
        b,
        index: i,
        level,
      });
    }
  };
  seq(input.aiPath, "ai", display.ai);
  seq(input.optimalPath, "gold", display.optimal);
  seq(input.suggestedPath, "localOptimal", display.suggested);
  seq(playerPath, "player", display.player);

  return { nodes, links, current, won, options: optionsFor(input, present) };
}

function optionsFor(input: BoardInput, present: Set<string>): BoardOption[] {
  const current = input.currentWord;
  if (input.status !== "playing" || !current) return [];
  if (current === input.targetWord) return [];
  const out: BoardOption[] = [];
  for (const id of input.options ?? []) {
    if (id === current) continue;
    const pos = input.position(id);
    if (!pos) continue;
    out.push({ id, x: pos[0], y: pos[1], existing: present.has(id) });
  }
  return out;
}
